import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Container,
  useTheme,
  alpha,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import apiService from '../services/api';
import MemberSearch from '../components/MemberSearch/MemberSearch';
import { PaymentFormData, MembershipPlan } from '../types/Payment';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import SaveIcon from '@mui/icons-material/Save';

// Function to calculate pro rata based on current date, rate, and payment frequency
const calculateProRata = (rate: number, paymentFrequency: 'Monthly' | 'Annual'): number => {
  if (!rate || rate <= 0) return 0;
  
  const today = new Date();
  const currentYear = today.getFullYear();
  
  if (paymentFrequency === 'Monthly') {
    // Calculate pro rata for the current month
    const currentMonth = today.getMonth();
    
    // Get the last day of current month
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    // Get current day of month
    const currentDay = today.getDate();
    
    // Calculate remaining days in the month (including today)
    const remainingDays = lastDayOfMonth - currentDay + 1;
    
    // Calculate pro rata amount
    const dailyRate = rate / lastDayOfMonth;
    const proRataAmount = dailyRate * remainingDays;
    
    return Math.round(proRataAmount * 100) / 100; // Round to 2 decimal places
  } else if (paymentFrequency === 'Annual') {
    // Calculate pro rata for the current year
    const startOfYear = new Date(currentYear, 0, 1);
    const endOfYear = new Date(currentYear, 11, 31);
    
    // Calculate total days in the year
    const totalDaysInYear = Math.ceil((endOfYear.getTime() - startOfYear.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // Calculate remaining days in the year (including today)
    const remainingDaysInYear = Math.ceil((endOfYear.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    // Calculate pro rata amount for remaining year
    const dailyRate = rate / totalDaysInYear;
    const proRataAmount = dailyRate * remainingDaysInYear;
    
    return Math.round(proRataAmount * 100) / 100; // Round to 2 decimal places
  }
  
  return 0;
};

const membershipPlans: MembershipPlan[] = [
  {
    id: 'home-owner',
    name: 'Home Owner (H/O)',
    type: 'Home Owner (H/O)',
    monthlyRate: 540,
    annualRate: 6480,
    features: ['Full gym access', 'Locker access', 'Resident benefits'],
    description: 'Special rate for home owners in the community',
    isActive: true
  },
  {
    id: 'outside-member',
    name: 'Outside Member',
    type: 'Outside Member',
    monthlyRate: 720,
    annualRate: 8640,
    features: ['Full gym access', 'Premium amenities', 'All classes'],
    description: 'Premium membership for external members',
    isActive: true
  },
  {
    id: 'tenant',
    name: 'Tenant',
    type: 'Tenant',
    monthlyRate: 450,
    annualRate: 5400,
    features: ['Full gym access', 'Basic amenities', 'Standard classes'],
    description: 'Affordable rate for tenants in the community',
    isActive: true
  }
];

const AddPayment: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<MembershipPlan | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();

  // Get pre-selected member ID from URL params
  const preSelectedMemberId = searchParams.get('memberId');

  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<PaymentFormData>({
    defaultValues: {
      selectedMember: null,
      memberSearch: '',
      amount: 0,
      paymentMethod: 'cash',
      paymentType: 'new_membership',
      paymentDate: new Date().toISOString().split('T')[0],
      receiptNumber: '',
      notes: '',
      membershipPlan: '',
      membershipDuration: 12,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      proRataAmount: 0,
      totalAmount: 0,
    },
  });

  // Watch form values for calculations
  const watchedValues = watch();
  const { selectedMember, paymentType, membershipPlan, membershipDuration, amount, proRataAmount } = watchedValues;

  // Pre-load member if specified in URL
  useEffect(() => {
    if (preSelectedMemberId) {
      // Fetch member details and set as selected
      const fetchMember = async () => {
        try {
          const response = await apiService.getMember(preSelectedMemberId);
          if (response.member) {
            setValue('selectedMember', response.member);
          }
        } catch (err) {
          console.error('Failed to fetch pre-selected member:', err);
        }
      };
      fetchMember();
    }
  }, [preSelectedMemberId, setValue]);

  // Update selected plan when membership plan changes
  useEffect(() => {
    if (membershipPlan) {
      const plan = membershipPlans.find(p => p.id === membershipPlan);
      setSelectedPlan(plan || null);
      
      if (plan) {
        // Calculate amount based on payment type and duration
        let calculatedAmount = 0;
        if (paymentType === 'new_membership' || paymentType === 'renewal') {
          if (membershipDuration === 12) {
            calculatedAmount = plan.annualRate;
          } else {
            calculatedAmount = plan.monthlyRate * membershipDuration;
          }
        }
        setValue('amount', calculatedAmount);
      }
    }
  }, [membershipPlan, membershipDuration, paymentType, setValue]);

  // Calculate pro-rata amount when plan or duration changes
  useEffect(() => {
    if (membershipPlan && (paymentType === 'new_membership' || paymentType === 'renewal')) {
      const plan = membershipPlans.find(p => p.id === membershipPlan);
      if (plan) {
        const paymentFrequency = membershipDuration === 12 ? 'Annual' : 'Monthly';
        const rate = paymentFrequency === 'Annual' ? plan.annualRate : plan.monthlyRate;
        const proRata = calculateProRata(rate, paymentFrequency);
        setValue('proRataAmount', proRata);
      }
    } else {
      setValue('proRataAmount', 0);
    }
  }, [membershipPlan, membershipDuration, paymentType, setValue]);

  // Calculate total amount
  useEffect(() => {
    const total = (amount || 0) + (proRataAmount || 0);
    setValue('totalAmount', total);
  }, [amount, proRataAmount, setValue]);

  // Update end date when start date or duration changes
  useEffect(() => {
    if (watchedValues.startDate && membershipDuration) {
      const startDate = new Date(watchedValues.startDate);
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + membershipDuration);
      setValue('endDate', endDate.toISOString().split('T')[0]);
    }
  }, [watchedValues.startDate, membershipDuration, setValue]);

  const onSubmit = async (data: PaymentFormData) => {
    if (!data.selectedMember) {
      setError('Please select a member');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const paymentData = {
        memberId: data.selectedMember._id,
        amount: data.totalAmount,
        paymentMethod: data.paymentMethod,
        paymentType: data.paymentType,
        paymentDate: data.paymentDate,
        receiptNumber: data.receiptNumber,
        notes: data.notes,
        membershipPlan: data.membershipPlan,
        membershipPeriod: {
          start: data.startDate,
          end: data.endDate,
        },
      };

      await apiService.createPayment(paymentData);
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
      
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const generateReceiptNumber = () => {
    const prefix = 'RCP';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}${timestamp}${random}`;
  };

  const handleGenerateReceipt = () => {
    setValue('receiptNumber', generateReceiptNumber());
  };

  if (success) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            Payment processed successfully! Redirecting to dashboard...
          </Alert>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper component="form" onSubmit={handleSubmit(onSubmit)} sx={{ p: 3 }}>
        <Grid container spacing={4}>
          {/* Member Selection Section */}
          <Grid item xs={12}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'white',
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <PersonIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      1. Select Member
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Search and select the member for payment processing
                    </Typography>
                  </Box>
                </Box>

                <Controller
                  name="selectedMember"
                  control={control}
                  rules={{ required: 'Please select a member' }}
                  render={({ field }) => (
                    <MemberSearch
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors.selectedMember}
                      helperText={errors.selectedMember?.message as string}
                      disabled={loading}
                    />
                  )}
                />

                {selectedMember && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Selected Member:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label={`${selectedMember.membershipType} Member`} color="primary" />
                      <Chip label={selectedMember.membershipStatus} color={selectedMember.membershipStatus === 'active' ? 'success' : 'error'} />
                      <Chip label={selectedMember.paymentStatus} color={selectedMember.paymentStatus === 'current' ? 'success' : 'warning'} />
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Details Section */}
          <Grid item xs={12}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.main',
                    color: 'white',
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <PaymentIcon />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                      2. Payment Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure payment details and membership terms
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Controller
                      name="paymentType"
                      control={control}
                      rules={{ required: 'Payment type is required' }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.paymentType}>
                          <InputLabel>Payment Type</InputLabel>
                          <Select {...field} label="Payment Type" disabled={loading}>
                            <MenuItem value="new_membership">New Membership</MenuItem>
                            <MenuItem value="renewal">Membership Renewal</MenuItem>
                            <MenuItem value="upgrade">Membership Upgrade</MenuItem>
                            <MenuItem value="partial">Partial Payment</MenuItem>
                            <MenuItem value="joining_fee">Joining Fee</MenuItem>
                            <MenuItem value="access_fee">Access Fee</MenuItem>
                            <MenuItem value="refund">Refund</MenuItem>
                          </Select>
                          {errors.paymentType && <FormHelperText>{errors.paymentType.message}</FormHelperText>}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Controller
                      name="paymentMethod"
                      control={control}
                      rules={{ required: 'Payment method is required' }}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.paymentMethod}>
                          <InputLabel>Payment Method</InputLabel>
                          <Select {...field} label="Payment Method" disabled={loading}>
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="check">Check</MenuItem>
                            <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                          {errors.paymentMethod && <FormHelperText>{errors.paymentMethod.message}</FormHelperText>}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Controller
                      name="paymentDate"
                      control={control}
                      rules={{ required: 'Payment date is required' }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Payment Date"
                          type="date"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          error={!!errors.paymentDate}
                          helperText={errors.paymentDate?.message}
                          disabled={loading}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Controller
                      name="receiptNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Receipt Number"
                          fullWidth
                          placeholder="Auto-generate or enter manually"
                          disabled={loading}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Button size="small" onClick={handleGenerateReceipt}>
                                  Generate
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {(paymentType === 'new_membership' || paymentType === 'renewal' || paymentType === 'upgrade') && (
                    <>
                      <Grid item xs={12} sm={6} md={4}>
                        <Controller
                          name="membershipPlan"
                          control={control}
                          rules={{ required: 'Membership plan is required' }}
                          render={({ field }) => (
                            <FormControl fullWidth error={!!errors.membershipPlan}>
                              <InputLabel>Membership Plan</InputLabel>
                              <Select {...field} label="Membership Plan" disabled={loading}>
                                {membershipPlans.map((plan) => (
                                  <MenuItem key={plan.id} value={plan.id}>
                                    {plan.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {errors.membershipPlan && <FormHelperText>{errors.membershipPlan.message}</FormHelperText>}
                            </FormControl>
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Controller
                          name="membershipDuration"
                          control={control}
                          render={({ field }) => (
                            <FormControl fullWidth>
                              <InputLabel>Duration</InputLabel>
                              <Select {...field} label="Duration" disabled={loading}>
                                <MenuItem value={1}>1 Month</MenuItem>
                                <MenuItem value={3}>3 Months</MenuItem>
                                <MenuItem value={6}>6 Months</MenuItem>
                                <MenuItem value={12}>12 Months (Annual)</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Controller
                          name="startDate"
                          control={control}
                          rules={{ required: 'Start date is required' }}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Membership Start Date"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              error={!!errors.startDate}
                              helperText={errors.startDate?.message}
                              disabled={loading}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Controller
                          name="endDate"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Membership End Date"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                              disabled={true} // Auto-calculated
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name="amount"
                      control={control}
                      rules={{ required: 'Amount is required', min: { value: 0.01, message: 'Amount must be greater than 0' } }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Payment Amount"
                          type="number"
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R</InputAdornment>,
                          }}
                          error={!!errors.amount}
                          helperText={errors.amount?.message}
                          disabled={loading}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Controller
                      name="proRataAmount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Pro Rata Amount"
                          type="number"
                          fullWidth
                          InputProps={{
                            startAdornment: <InputAdornment position="start">R</InputAdornment>,
                          }}
                          disabled={loading}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value === '' ? '' : Number(e.target.value))}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Payment Notes"
                          multiline
                          rows={3}
                          fullWidth
                          placeholder="Add any additional notes about this payment..."
                          disabled={loading}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                {/* Plan Details */}
                {selectedPlan && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
                      Selected Plan: {selectedPlan.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedPlan.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {selectedPlan.features.map((feature, index) => (
                        <Chip key={index} label={feature} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Total Amount Display */}
                <Box sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.main'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Total Amount: R {watchedValues.totalAmount?.toFixed(2) || '0.00'}
                  </Typography>
                  {watchedValues.amount > 0 && watchedValues.proRataAmount > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Payment: R{watchedValues.amount?.toFixed(2)} + Pro Rata: R{watchedValues.proRataAmount?.toFixed(2)}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                disabled={loading || !selectedMember}
                sx={{ minWidth: 120 }}
              >
                {loading ? 'Processing...' : 'Process Payment'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default AddPayment;
